import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const LegalPage = () => {
  const navigate = useNavigate();
  const { docType } = useParams();

  const legalContent = {
    terms: {
      title: "Terms of Use and End User Agreement",
      content: `
        <p><strong>Effective Date:</strong> October 17, 2025</p>
        
        <p>Welcome to Daily Discipline, a habit-building app provided by Pam Matambanadzo, doing business as Alignify Now ("we," "us," or "our"). These Terms of Use govern your access to and use of the Daily Discipline web and mobile applications (the "Service").</p>
        
        <h3>1. Eligibility and Account Creation</h3>
        <p>To use Daily Discipline, you must be at least 16 years old (or the minimum age in your jurisdiction). By registering, you agree to provide accurate account information. You are responsible for safeguarding your login credentials.</p>
        
        <h3>2. Free Trial and Subscription</h3>
        <p>We offer a 14-day free trial. After the trial ends, continued access requires a paid subscription. Subscription terms, pricing, and renewal conditions will be presented at checkout. You may cancel renewal at any time, but we do not offer refunds for partial billing periods.</p>
        
        <h3>3. App Purpose and Health Disclaimer</h3>
        <p>Daily Discipline is a self-improvement and productivity tool designed to support habit formation. The app offers general prompts, reflections, and AI-generated suggestions intended for everyday life routines (e.g., exercise, cleaning, time management).</p>
        <p><strong>We are not licensed medical professionals, psychologists, or therapists.</strong> The content and suggestions within the app are <strong>not</strong> medical, therapeutic, or psychological advice, and are <strong>not</strong> intended to diagnose, treat, or manage any mental or physical health condition.</p>
        <p>If you are experiencing a mental health issue, consult a qualified health professional. <strong>This app is not suitable for individuals seeking treatment or guidance for mental health disorders.</strong></p>
        
        <h3>4. User Content</h3>
        <p>You may input comments and reflections on tasks you create. You are solely responsible for your content. Do not input sensitive, personal, or confidential information into the app. We reserve the right to remove content that violates these Terms or applicable law.</p>
        
        <h3>5. Acceptable Use</h3>
        <p>You agree not to misuse the Service or access it in any unlawful way. You will not reverse-engineer, interfere with, or attempt unauthorized access to our systems.</p>
        
        <h3>6. Intellectual Property</h3>
        <p>All content and software are owned by us or our licensors. You may not copy, modify, or distribute any part of the Service without permission.</p>
        
        <h3>7. Limitation of Liability</h3>
        <p>To the fullest extent allowed by law, we disclaim all warranties. The Service is provided "as is" and "as available." We are not liable for any damages, including but not limited to indirect, incidental, or consequential damages. If you are dissatisfied with the Service, your sole remedy is to stop using it.</p>
        
        <h3>8. Indemnification</h3>
        <p>You agree to indemnify and hold harmless Alignify Now and Pam Matambanadzo from any claims arising out of your use of the Service, your content, or your breach of these Terms.</p>
        
        <h3>9. Termination</h3>
        <p>We may suspend or terminate your access if you violate these Terms or applicable laws. You may cancel your subscription and delete your account at any time.</p>
        
        <h3>10. Governing Law</h3>
        <p>These Terms are governed by the laws of the State of Illinois, United States, without regard to conflict of law principles.</p>
        
        <h3>11. Contact</h3>
        <p>For questions or concerns, contact us at:<br/>
        Email: <a href="mailto:privacy@dailydiscipline.app" class="text-emerald-600 underline">privacy@dailydiscipline.app</a></p>
        
        <p><em>By creating an account or using the Service, you agree to these Terms.</em></p>
        
        <p class="mt-8 text-gray-500">© 2025 Alignify Now. All rights reserved.</p>
      `
    },
    privacy: {
      title: "Privacy Policy",
      content: `
        <p><strong>Effective Date:</strong> October 17, 2025</p>
        
        <p>Your privacy is critically important to us. Daily Discipline does not sell your personal data.</p>
        
        <h3>Information We Collect</h3>
        <p>We collect information you provide directly, such as:</p>
        <ul>
          <li>Account information (email, name)</li>
          <li>Profile preferences (timezone)</li>
          <li>Habit tracking data you input</li>
          <li>Communications with our AI coach</li>
        </ul>
        
        <h3>How We Use Your Information</h3>
        <p>We use your information to:</p>
        <ul>
          <li>Provide and improve the Service</li>
          <li>Send you reminders and notifications</li>
          <li>Process payments and subscriptions</li>
          <li>Respond to your inquiries</li>
        </ul>
        
        <h3>Data Security</h3>
        <p>We implement appropriate security measures to protect your personal information. However, no method of transmission over the Internet is 100% secure.</p>
        
        <h3>Third-Party Services</h3>
        <p>We use third-party services for:</p>
        <ul>
          <li>Authentication (Google OAuth)</li>
          <li>Payment processing (Stripe)</li>
          <li>Email delivery (Brevo)</li>
          <li>AI services (OpenAI)</li>
        </ul>
        
        <h3>Your Rights</h3>
        <p>You may:</p>
        <ul>
          <li>Access, update, or delete your account information</li>
          <li>Request a copy of your data</li>
          <li>Opt out of promotional communications</li>
        </ul>
        
        <h3>Contact</h3>
        <p>For privacy-related questions, contact us at:<br/>
        Email: <a href="mailto:privacy@dailydiscipline.app" class="text-emerald-600 underline">privacy@dailydiscipline.app</a></p>
        
        <p class="mt-8 text-gray-500">© 2025 Alignify Now. All rights reserved.</p>
      `
    },
    refund: {
      title: "Refund & Billing Policy",
      content: `
        <p><strong>Effective Date:</strong> October 17, 2025</p>
        
        <h3>Free Trial</h3>
        <p>We offer a 14-day free trial so you can explore the app before subscribing. You may cancel anytime during the trial to avoid charges.</p>
        
        <h3>Subscriptions</h3>
        <p>After the trial, you'll be billed according to the plan you select (monthly or annually). Charges are processed at the start of each billing period.</p>
        
        <h3>No Refunds</h3>
        <p>All purchases are final. We do not offer refunds for unused time, accidental purchases, or dissatisfaction once the subscription period begins. You may cancel at any time to prevent future billing.</p>
        
        <h3>Cancellations</h3>
        <p>You can cancel via your account settings, app store, or contact us at <a href="mailto:privacy@dailydiscipline.app" class="text-emerald-600 underline">privacy@dailydiscipline.app</a>. Cancellation stops the next renewal — it does not refund the current period.</p>
        
        <p class="mt-8 text-gray-500">© 2025 Alignify Now. All rights reserved.</p>
      `
    }
  };

  const currentDoc = legalContent[docType] || legalContent.terms;

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-emerald-600 font-medium hover:text-emerald-800"
          >
            <span className="mr-2">←</span> Back
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="p-6 max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">{currentDoc.title}</h1>
        <div
          className="prose prose-emerald text-sm text-gray-600 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: currentDoc.content }}
        />
      </div>
    </div>
  );
};

export default LegalPage;
